import * as React from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";
import {
  AlertTriangle,
  Clock,
  CreditCard,
  Mail,
  MessageSquare,
  Phone,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { usePaymentStatusReport } from "@/hooks/useAccounting";
import { formatCurrency, formatDate } from "@/lib/format";
import {
  getEnterpriseBadgeClass,
  getEnterpriseBadgeLabel,
} from "@/lib/customer-tag-colors";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const BUCKET_ICONS = [Clock, Clock, AlertTriangle, AlertTriangle, AlertTriangle];
const BUCKET_COLORS = [
  "text-yellow-600 dark:text-yellow-400",
  "text-orange-600 dark:text-orange-400",
  "text-red-500 dark:text-red-400",
  "text-red-600 dark:text-red-400",
  "text-red-800 dark:text-red-300",
];

export function AgingTab() {
  const report = usePaymentStatusReport();
  const [selectedBucket, setSelectedBucket] = React.useState<number | null>(
    null,
  );
  const [chargePayment, setChargePayment] = React.useState<any>(null);
  const [logPayment, setLogPayment] = React.useState<any>(null);

  const buckets = report?.buckets ?? [];
  const payments = React.useMemo(() => {
    if (selectedBucket === null) {
      return buckets.flatMap((b: any) => b.payments ?? []);
    }
    return buckets[selectedBucket]?.payments ?? [];
  }, [buckets, selectedBucket]);

  return (
    <div className="space-y-6">
      {/* Aging Buckets Summary */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {buckets.map((bucket: any, idx: number) => {
          const Icon = BUCKET_ICONS[idx] ?? Clock;
          const color = BUCKET_COLORS[idx] ?? "text-gray-600 dark:text-gray-300";
          const isActive = selectedBucket === idx;
          return (
            <Card
              key={bucket.label}
              className={`cursor-pointer transition-colors ${isActive ? "ring-2 ring-primary" : "hover:bg-muted/50"}`}
              onClick={() =>
                setSelectedBucket(isActive ? null : idx)
              }
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Icon className={`h-4 w-4 ${color}`} />
                  <p className="text-xs font-medium text-muted-foreground">
                    {bucket.label}
                  </p>
                </div>
                <p className="mt-1 text-lg font-bold">
                  {formatCurrency(bucket.totalAmount ?? 0)}
                </p>
                <Badge variant="secondary" className="mt-1">
                  {bucket.count ?? 0} payments
                </Badge>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Summary Stats */}
      {report ? (
        <div className="flex gap-4 text-sm text-muted-foreground">
          <span>
            Total overdue:{" "}
            <span className="font-semibold text-foreground">
              {formatCurrency(report.totalOverdue ?? 0)}
            </span>
          </span>
          <span>
            Overdue count:{" "}
            <span className="font-semibold text-foreground">
              {report.totalOverdueCount ?? 0}
            </span>
          </span>
        </div>
      ) : null}

      {/* Overdue Payments Table */}
      <Card>
        <CardHeader>
          <CardTitle>Overdue Payments</CardTitle>
          <CardDescription>
            {selectedBucket !== null
              ? `Showing: ${buckets[selectedBucket]?.label}`
              : "All overdue payments"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {report === undefined ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Loading payment data...</div>
          ) : payments.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No overdue payments found
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="text-muted-foreground hover:bg-transparent">
                  <TableHead className="font-medium">Customer</TableHead>
                  <TableHead className="font-medium">Enterprise</TableHead>
                  <TableHead className="font-medium">Order</TableHead>
                  <TableHead className="font-medium text-right">Amount</TableHead>
                  <TableHead className="font-medium">Due Date</TableHead>
                  <TableHead className="font-medium">Status</TableHead>
                  <TableHead className="font-medium">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((p: any) => (
                  <TableRow key={p.scheduledPaymentId}>
                    <TableCell className="min-w-[220px]">
                      <p className="text-sm font-medium">
                        {p.customerName || "Unknown"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {p.customerEmail}
                      </p>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={getEnterpriseBadgeClass(p.isEnterprise)}
                      >
                        {getEnterpriseBadgeLabel(p.isEnterprise)}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {p.orderNumber || "—"}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {formatCurrency(p.amount)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {p.dueDate ? formatDate(p.dueDate) : "—"}
                    </TableCell>
                    <TableCell>
                      {p.status === "failed" ? (
                        <Badge
                          variant="outline"
                          className="border-red-300 bg-red-100 text-red-900 dark:border-red-800/60 dark:bg-red-950/40 dark:text-red-200"
                        >
                          Failed
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200"
                        >
                          {p.daysOverdue}d overdue
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            Actions
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {p.orderId ? (
                            <DropdownMenuItem
                              onClick={() =>
                                window.open(
                                  `/invoice/${p.orderId}`,
                                  "_blank",
                                  "noopener,noreferrer",
                                )
                              }
                            >
                              View Invoice (Internal)
                            </DropdownMenuItem>
                          ) : null}
                          <DropdownMenuItem onClick={() => setLogPayment(p)}>
                            Log Communication
                          </DropdownMenuItem>
                          {p.daysOverdue > 0 ? (
                            <DropdownMenuItem
                              onClick={() => setChargePayment(p)}
                            >
                              Apply Charge
                            </DropdownMenuItem>
                          ) : null}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {chargePayment ? (
        <ApplyChargeDialog
          payment={chargePayment}
          onClose={() => setChargePayment(null)}
        />
      ) : null}
      {logPayment ? (
        <LogCommunicationDialog
          payment={logPayment}
          onClose={() => setLogPayment(null)}
        />
      ) : null}
    </div>
  );
}

function ApplyChargeDialog({
  payment,
  onClose,
}: {
  payment: any;
  onClose: () => void;
}) {
  const applyCharge = useMutation(api.aging.applyArCharge);
  const lateFee = useQuery(
    api.aging.calculateLateFee,
    payment.orderId
      ? {
          orderId: payment.orderId,
          scheduledPaymentId: payment.scheduledPaymentId,
        }
      : "skip",
  );

  const [chargeType, setChargeType] = React.useState<
    "late_fee" | "nsf_fee" | "ach_reversal"
  >("late_fee");
  const [customAmount, setCustomAmount] = React.useState("");
  const [bankCharges, setBankCharges] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [generateLink, setGenerateLink] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  const calculatedAmount = React.useMemo(() => {
    if (chargeType === "late_fee") {
      return customAmount ? parseFloat(customAmount) : (lateFee?.amount ?? 0);
    }
    if (chargeType === "nsf_fee") {
      return 50 + (parseFloat(bankCharges) || 0);
    }
    return 50; // ach_reversal
  }, [chargeType, customAmount, bankCharges, lateFee]);

  const handleApply = React.useCallback(async () => {
    setIsSaving(true);
    try {
      await applyCharge({
        orderId: payment.orderId,
        scheduledPaymentId: payment.scheduledPaymentId,
        type: chargeType,
        amount: calculatedAmount,
        calculatedAmount: lateFee?.amount,
        calculationMethod: lateFee?.method,
        monthsOverdue: lateFee?.months,
        bankCharges: bankCharges ? parseFloat(bankCharges) : undefined,
        notes: notes || undefined,
        generatePaymentLink: generateLink,
        sendNotification: generateLink,
      });
      toast.success("Charge applied");
      onClose();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to apply charge",
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    payment,
    chargeType,
    calculatedAmount,
    lateFee,
    bankCharges,
    notes,
    generateLink,
    applyCharge,
    onClose,
  ]);

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Apply Charge</DialogTitle>
          <DialogDescription>
            {payment.customerName} · Order {payment.orderNumber} ·{" "}
            {formatCurrency(payment.amount)}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Charge Type</Label>
            <div className="flex gap-2">
              {(
                [
                  ["late_fee", "Late Fee"],
                  ["nsf_fee", "NSF Fee"],
                  ["ach_reversal", "ACH Reversal"],
                ] as const
              ).map(([val, label]) => (
                <Button
                  key={val}
                  variant={chargeType === val ? "default" : "outline"}
                  size="sm"
                  onClick={() => setChargeType(val)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>

          {chargeType === "late_fee" ? (
            <div className="space-y-2">
              {lateFee ? (
                <p className="text-sm text-muted-foreground">
                  Calculated: {formatCurrency(lateFee.amount)} (
                  {lateFee.method === "flat_50"
                    ? "$50 minimum"
                    : `3%/mo × ${lateFee.months}mo`}
                  )
                </p>
              ) : null}
              <div className="grid gap-2">
                <Label>Custom Amount (optional)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder="Leave blank for calculated amount"
                />
              </div>
            </div>
          ) : null}

          {chargeType === "nsf_fee" ? (
            <div className="grid gap-2">
              <Label>Additional Bank Charges ($)</Label>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={bankCharges}
                onChange={(e) => setBankCharges(e.target.value)}
                placeholder="0.00"
              />
              <p className="text-xs text-muted-foreground">
                Base NSF fee: $50 + bank charges
              </p>
            </div>
          ) : null}

          {chargeType === "ach_reversal" ? (
            <p className="text-sm text-muted-foreground">
              Flat $50 ACH reversal fee
            </p>
          ) : null}

          <div className="grid gap-2">
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes..."
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="genLink"
              checked={generateLink}
              onChange={(e) => setGenerateLink(e.target.checked)}
              className="h-4 w-4"
            />
            <Label htmlFor="genLink" className="text-sm">
              Generate payment link and email customer
            </Label>
          </div>

          <div className="rounded border p-3 text-sm">
            <p className="font-medium">
              Total charge: {formatCurrency(calculatedAmount)}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleApply()}
            disabled={isSaving || calculatedAmount <= 0}
          >
            {isSaving ? "Applying..." : "Apply Charge"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LogCommunicationDialog({
  payment,
  onClose,
}: {
  payment: any;
  onClose: () => void;
}) {
  const logComm = useMutation(api.arCommunications.logCommunication);
  const [type, setType] = React.useState<"email" | "phone" | "note">("email");
  const [summary, setSummary] = React.useState("");
  const [details, setDetails] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);

  const handleSave = React.useCallback(async () => {
    if (!summary.trim()) {
      toast.error("Summary is required");
      return;
    }
    setIsSaving(true);
    try {
      await logComm({
        userId: payment.userId,
        orderId: payment.orderId,
        type,
        summary,
        details: details || undefined,
      });
      toast.success("Communication logged");
      onClose();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to log communication",
      );
    } finally {
      setIsSaving(false);
    }
  }, [payment, type, summary, details, logComm, onClose]);

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Log Communication</DialogTitle>
          <DialogDescription>
            {payment.customerName} · {payment.customerEmail}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Type</Label>
            <div className="flex gap-2">
              {(
                [
                  ["email", "Email", Mail],
                  ["phone", "Phone Call", Phone],
                  ["note", "Internal Note", MessageSquare],
                ] as const
              ).map(([val, label, Icon]) => (
                <Button
                  key={val}
                  variant={type === val ? "default" : "outline"}
                  size="sm"
                  className="gap-1"
                  onClick={() => setType(val as any)}
                >
                  <Icon className="h-3 w-3" />
                  {label}
                </Button>
              ))}
            </div>
          </div>
          <div className="grid gap-2">
            <Label>Summary (required)</Label>
            <Input
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Brief summary of the communication..."
            />
          </div>
          <div className="grid gap-2">
            <Label>Details (optional)</Label>
            <Textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Additional details..."
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={isSaving || !summary.trim()}
          >
            {isSaving ? "Saving..." : "Log Communication"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
